import axios, { AxiosError } from "axios";

import { AnsweredQuestionWithId } from "@teamprevent/common/model";
import { PhysiotherapyReferralReportDetails } from "@teamprevent/common/physiotherapyReferralReport/models";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { ConsultationDetails, parseConsultationModel } from "@teamprevent/common/consultations/models";

import { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { parseTaskModel, AddTaskModel, CreateTaskDto } from "../task/model";

import { mapAddTaskModelToCreateTaskDto, mapCreateTaskDtoToServer } from "../crud/ohCase/ohCaseApiMappers";

import { PhysiotherapyReferralReportModel } from "./models";

const buildUrl = (ohCaseId: string, taskId: string) =>
    `/api/physiotherapyReferralReport/ohCase/${ohCaseId}/task/${taskId}`;

const getReport = async (ohCaseId: string, taskId: string): Promise<PhysiotherapyReferralReportDetails | null> => {
    try {
        const response = await axios.get<PhysiotherapyReferralReportDetails>(buildUrl(ohCaseId, taskId));
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError && axiosError.response && axiosError.response.status === 404) {
            return null;
        }

        throw ex;
    }
};

const getReportV2 = async (ohCaseId: string, taskId: string) => {
    try {
        const response = await axios.get<PhysiotherapyReferralReportModel>(`${buildUrl(ohCaseId, taskId)}/v2`);
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return null;
        }

        throw ex;
    }
};

const getConsultation = async (ohCaseId: string, taskId: string) => {
    try {
        const response = await axios.get<
            ServerModelWithDateStrings<ConsultationDetails>
        >(`${buildUrl(ohCaseId, taskId)}/consultation`);
        return parseConsultationModel(response.data);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return null;
        }

        throw ex;
    }
};

const getReportDetails = async (ohCaseId: string, taskId: string): Promise<PhysiotherapyReferralReportDetails | null> => {
    try {
        const response = await axios.get(`${buildUrl(ohCaseId, taskId)}/reportDetails`);
        return response.data as PhysiotherapyReferralReportDetails;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return null;
        }

        throw ex;
    }
};

const saveReport = async (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string) => {
    try {
        return (await axios.post<PhysiotherapyReferralReportDetails>(buildUrl(ohCaseId, taskId), { data: { questions }, eTag })).data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError.response?.status === 400) {
            const error = { message: "Could not save as a newer version exists", status: 400 };
            throw error;
        } else {
            throw ex;
        }
    }
};

const submitReport = async (ohCaseId: string, taskId: string, tasks: CreateTaskDto[]): Promise<TaskModel[] | null> => {
    try {
        const createTasks = (tasks ?? []).map(nextTask => mapCreateTaskDtoToServer(nextTask));

        const response = await axios.post<ServerModelWithDateStrings<TaskModel>[]>(`${buildUrl(ohCaseId, taskId)}/successorTasks`, createTasks);
        return response.data?.map(parseTaskModel);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return [];
        }

        throw ex;
    }
};

const submitReportV2 = async (ohCaseId: string, taskId: string, tasks: AddTaskModel[]): Promise<TaskModel[] | null> => {
    try {
        const createTasks = (tasks ?? []).map(nextTask => mapCreateTaskDtoToServer(mapAddTaskModelToCreateTaskDto(nextTask)));

        const response = await axios.post<ServerModelWithDateStrings<TaskModel>[]>(`${buildUrl(ohCaseId, taskId)}/successorTasks`, createTasks);
        return response.data?.map(parseTaskModel);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return [];
        }

        throw ex;
    }
};
const submitReportAmendment = async (ohCaseId: string, taskId: string, tasks: AddTaskModel[]): Promise<TaskModel[] | null> => {
    try {
        const createTasks = (tasks ?? []).map(nextTask => mapCreateTaskDtoToServer(mapAddTaskModelToCreateTaskDto(nextTask)));

        const response = await axios.post<ServerModelWithDateStrings<TaskModel>[]>(`${buildUrl(ohCaseId, taskId)}/completeAmendments`, createTasks);
        return response.data?.map(parseTaskModel);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return [];
        }

        throw ex;
    }
};

const approve = async (ohCaseId: string, taskId: string, notes: string): Promise<TaskModel[] | null> => {
    try {
        const response = await axios.post<ServerModelWithDateStrings<TaskModel[]>>(`${buildUrl(ohCaseId, taskId)}/approve`, { notes });
        return response.data?.map(parseTaskModel);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return null;
        }

        throw ex;
    }
};

const requestChanges = async (ohCaseId: string, taskId: string, notes: string): Promise<TaskModel[] | null> => {
    try {
        const response = await axios.post<ServerModelWithDateStrings<TaskModel[]>>(`${buildUrl(ohCaseId, taskId)}/requestchanges`, { notes });
        return response.data?.map(parseTaskModel);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return null;
        }

        throw ex;
    }
};

const reject = async (ohCaseId: string, taskId: string, notes: string): Promise<TaskModel[] | null> => {
    try {
        const response = await axios.post<ServerModelWithDateStrings<TaskModel[]>>(`${buildUrl(ohCaseId, taskId)}/reject`, { notes });
        return response.data?.map(parseTaskModel);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError?.response?.status === 404) {
            return null;
        }

        throw ex;
    }
};

export const physiotherapyReferralReportApi = {
    getReport,
    getReportV2,
    getConsultation,
    getReportDetails,
    saveReport,
    submitReportV2,
    submitReport,
    submitReportAmendment,
    approve,
    requestChanges,
    reject
};
